import { BadgeCheckIcon, KeyIcon } from "@heroicons/react/solid";
import { Field, FormikHelpers, FormikProps } from "formik";
import Tooltip from "rc-tooltip";
import React, { useCallback, useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import * as Yup from "yup";
import AlertMessage from "../../../../components/AlertMessage";
import FormProvider from "../../../../components/forms/FormProvider";
import Input from "../../../../components/forms/Input";
import MessageComponent from "../../../../components/message";
import { useAuthentication } from "../../../../context/authentication";
import { useHotspots } from "../../../../context/hotspots";
import { useMessages } from "../../../../context/messages";
import { useWallets } from "../../../../context/wallets";
import { formatHotspotName } from "../../../../utils/hotspotUtils";
import HotspotWalletLabel from "./HotspotWalletLabel";

const HotspotOwnerTab: React.FC<RouteComponentProps<{ id: string }>> = () => {
  const { currentHotspot } = useHotspots();
  const { myMessages, addMessage } = useMessages();
  const { wrapActionWithAuth, authenticationState } = useAuthentication();
  const { verifiedWalletsCount } = useWallets();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      wrapActionWithAuth(async () => {
        try {
          if (verifiedWalletsCount < 1) {
            setErrorMessage("Please verify a wallet first");
          } else {
            await addMessage(formValues.message, currentHotspot?.id);
            formikHelpers.resetForm();
          }

          formikHelpers.setSubmitting(false);
        } catch (error) {
          setErrorMessage("Something went wrong");
          formikHelpers.setSubmitting(false);
        }
      })();
    },
    [addMessage, currentHotspot?.id, verifiedWalletsCount, wrapActionWithAuth]
  );

  if (!currentHotspot) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      {!currentHotspot.verified_by && (
        <div className="mt-6">
          {currentHotspot.total_messages > 0 ? (
            <>
              <h3 className="flex items-center text-lg leading-6 font-medium text-gray-900 dark:text-white">
                Inbox
                <span className="bg-gray-200 text-gray-500 dark:text-gray-200 dark:bg-gray-600 ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {currentHotspot.total_messages}
                </span>
              </h3>
              <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
                {formatHotspotName(currentHotspot)} has received{" "}
                {currentHotspot.total_messages} message
                {currentHotspot.total_messages === 1 ? "" : "s"}, which can be
                unlocked by verifying its wallet.
              </p>

              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                Is this your hotspot?
              </h3>
              <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
                If you own this hotspot, please verify the wallet it belongs to
                in order to unlock the message
                {currentHotspot.total_messages === 1 ? "" : "s"}.
              </p>
            </>
          ) : (
            <>
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                Is this your hotspot?
              </h3>
              <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
                Verify the wallet containing {formatHotspotName(currentHotspot)}{" "}
                to unlock further benefits such as the ability to be{" "}
                <Tooltip
                  placement="top"
                  overlay="Your private workspace channel will receive the message along with the sender's discord handle. We do not reveal your identity. It is up to you to reach out to the sender."
                >
                  <u className="cursor-default">contacted</u>
                </Tooltip>{" "}
                for optimisation improvements, warnings and recommendations for
                your hotspots, aggregated statistics...
              </p>
            </>
          )}

          <Link
            to={`/workspace/wallets/verify/${currentHotspot.owner}/confirm-wallet`}
            className="mt-5 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
          >
            <KeyIcon
              className="-ml-1 mr-2 h-5 w-5 text-white-500"
              aria-hidden="true"
            />
            <span>Verify wallet</span>
          </Link>
        </div>
      )}

      <div className="mt-6 pb-5">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
          Do you know the owner?
        </h3>
        <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
          Label the wallet of this hotspot so that you can more easily identify
          and filter hotspots of this owner.
        </p>
      </div>

      <HotspotWalletLabel hotspot={currentHotspot} />

      <div className="mt-6 pb-5">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
          Send a message to the owner
          {currentHotspot.verified_by && (
            <Tooltip
              placement="right"
              overlay={`${formatHotspotName(
                currentHotspot
              )}'s wallet has been verified so messages are directly delivered to the owner.`}
            >
              <BadgeCheckIcon className="inline-flex ml-2 h-6 w-6 text-green-400" />
            </Tooltip>
          )}
        </h3>
        <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
          Hotspot rewards are reduced by the transmit scale of hotspots it
          witnesses. This means that rewards can be increased by collaborating
          with other hotspot owners to improve the transmit scale of your area.
          Here you can send a message to the owner of this hotspot.
        </p>
      </div>

      {authenticationState !== "authenticated" && (
        <AlertMessage
          className="mt-2"
          type="warning"
          title="Login is required before sending messages"
        />
      )}

      {authenticationState === "authenticated" && verifiedWalletsCount < 1 && (
        <AlertMessage
          className="mt-2"
          type="warning"
          title="Minimum one wallet verification is required"
          message="Just like any other messaging platform, Hotspotty's messaging system is susceptible to abuse from bad actors. The purpose of the hotspot messaging system is to facilitate collaboration between hotspot owners to improve the People's Network together. A good example is collaboration between hotspot owners that are affected by problematic hexes so that these inefficiencies can be mitigated. For this reason we require at least one wallet verification by your workspace in order to send messages to other hotspot owners."
          collapsible
        />
      )}

      <div className="max-w-5xl mx-auto">
        {myMessages[currentHotspot.id] && (
          <>
            {myMessages[currentHotspot.id].map((message) => (
              <div className="py-4" key={message.uuid}>
                <MessageComponent message={message} />
              </div>
            ))}
          </>
        )}
      </div>

      <div className="w-full mt-2">
        <FormProvider
          error={errorMessage}
          submitButtonText={"Send a message"}
          submittingSubmitButtonText={"Sending"}
          classnames={{
            wrapper: "w-full sm:max-w-none",
            buttonWrapper: "pr-0 pb-0"
          }}
          formikProps={{
            onSubmit,
            initialValues: { message: "" },
            validationSchema: Yup.object().shape({
              message: Yup.string().required("Required")
            })
          }}
          orderedFieldNames={ORDERED_FIELD_NAMES}
        >
          {({ isSubmitting }: FormikProps<FormValues>) => {
            return (
              <Field
                id="message"
                name="message"
                placeholder="Type a message"
                component={Input}
                textarea
                withoutErrorLabel
                rows={3}
                styles={{
                  inputContainer: { maxWidth: "unset" }
                }}
                classnames={{
                  input: "w-full"
                }}
                disabled={isSubmitting}
              />
            );
          }}
        </FormProvider>
      </div>
    </div>
  );
};

export default HotspotOwnerTab;

//
// Utils
//

interface FormValues {
  message: string;
}

const ORDERED_FIELD_NAMES = ["message"];
